import { useState, useCallback, useMemo } from 'react'
import {
	Trash2,
	GripVertical,
	Image,
	Video,
	Mic,
	FileText,
	Smile,
	X,
	Upload,
	Loader2,
	AlertCircle,
} from 'lucide-react'
import { render as renderFlexMessage } from 'flex-render'
import type { FlexContainer } from 'flex-render'

import { cn } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/common/ui/collapsible'

import { ChatMessageType } from '@/schema'
import {
	StickerPicker,
	getStickerPreviewUrl,
} from '../../../../components/StickerPicker'

import {
	MESSAGE_TYPE_CONFIGS,
	getMessageTypeConfig,
	validateMessageContent,
} from '../../types'
import type { MessageContentInput } from '../../types'
import { useMutationUploadChatMedia } from '../../hooks/useMutationUploadChatMedia'

// Helper to get audio duration from file
function getAudioDuration(file: File): Promise<number> {
	return new Promise((resolve) => {
		const audio = new Audio()
		const objectUrl = URL.createObjectURL(file)

		audio.addEventListener('loadedmetadata', () => {
			// Duration is in seconds, convert to milliseconds
			const durationMs = Math.round(audio.duration * 1000)
			URL.revokeObjectURL(objectUrl)
			resolve(durationMs)
		})

		audio.addEventListener('error', () => {
			URL.revokeObjectURL(objectUrl)
			resolve(0) // Return 0 if we can't get duration
		})

		audio.src = objectUrl
	})
}

// Helper to generate video thumbnail from first frame
function getVideoThumbnail(file: File): Promise<File | null> {
	return new Promise((resolve) => {
		const video = document.createElement('video')
		const objectUrl = URL.createObjectURL(file)

		video.addEventListener('loadeddata', () => {
			// Seek to first frame
			video.currentTime = 0
		})

		video.addEventListener('seeked', () => {
			// Create canvas and draw video frame
			const canvas = document.createElement('canvas')
			canvas.width = video.videoWidth
			canvas.height = video.videoHeight

			const ctx = canvas.getContext('2d')
			if (!ctx) {
				URL.revokeObjectURL(objectUrl)
				resolve(null)
				return
			}

			ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

			// Convert canvas to blob
			canvas.toBlob(
				(blob) => {
					URL.revokeObjectURL(objectUrl)
					if (blob) {
						const thumbnailFile = new File([blob], 'thumbnail.jpg', {
							type: 'image/jpeg',
						})
						resolve(thumbnailFile)
					} else {
						resolve(null)
					}
				},
				'image/jpeg',
				0.8,
			)
		})

		video.addEventListener('error', () => {
			URL.revokeObjectURL(objectUrl)
			resolve(null)
		})

		video.preload = 'metadata'
		video.src = objectUrl
		video.load()
	})
}

interface MessageContentEditorProps {
	value: MessageContentInput
	onChange: (value: MessageContentInput) => void
	onDelete: () => void
	index: number
	isDragging?: boolean
	dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
}

export function MessageContentEditor({
	value,
	onChange,
	onDelete,
	index,
	isDragging,
	dragHandleProps,
}: MessageContentEditorProps) {
	const [isOpen, setIsOpen] = useState(true)
	const [showStickerPicker, setShowStickerPicker] = useState(false)

	const uploadMedia = useMutationUploadChatMedia()

	const typeConfig = getMessageTypeConfig(value.messageType)
	const isValid = validateMessageContent(value)

	const handleTypeChange = useCallback(
		(newType: ChatMessageType) => {
			// Reset all type-specific fields when changing type
			onChange({
				...value,
				messageType: newType,
				text: undefined,
				stickerPackageId: undefined,
				stickerId: undefined,
				imageUrl: undefined,
				videoUrl: undefined,
				videoPreviewUrl: undefined,
				audioUrl: undefined,
				audioDuration: undefined,
				fileUrl: undefined,
				fileName: undefined,
				locationTitle: undefined,
				locationAddress: undefined,
				locationLatitude: undefined,
				locationLongitude: undefined,
				flexAltText: undefined,
				flexContents: undefined,
			})
		},
		[value, onChange],
	)

	const handleFileUpload = useCallback(
		async (file: File, mediaType: 'image' | 'video' | 'audio' | 'file') => {
			try {
				const result = await uploadMedia.mutateAsync({ file, mediaType })

				switch (mediaType) {
					case 'image':
						onChange({ ...value, imageUrl: result.fileUrl })
						break
					case 'video': {
						// Generate thumbnail from video first frame
						const thumbnail = await getVideoThumbnail(file)
						let previewUrl = result.fileUrl

						if (thumbnail) {
							// Upload thumbnail as image
							const thumbnailResult = await uploadMedia.mutateAsync({
								file: thumbnail,
								mediaType: 'image',
							})
							previewUrl = thumbnailResult.fileUrl
						}

						onChange({
							...value,
							videoUrl: result.fileUrl,
							videoPreviewUrl: previewUrl,
						})
						break
					}
					case 'audio': {
						// Get audio duration from file
						const duration = await getAudioDuration(file)
						onChange({
							...value,
							audioUrl: result.fileUrl,
							audioDuration: duration,
						})
						break
					}
					case 'file':
						onChange({
							...value,
							fileUrl: result.fileUrl,
							fileName: file.name,
						})
						break
				}
			} catch {
				// Error is handled by the mutation
			}
		},
		[value, onChange, uploadMedia],
	)

	const handleStickerSelect = useCallback(
		(selection: { packageId: string; stickerId: string }) => {
			onChange({
				...value,
				stickerPackageId: selection.packageId,
				stickerId: selection.stickerId,
			})
			setShowStickerPicker(false)
		},
		[value, onChange],
	)

	const renderTypeFields = () => {
		switch (value.messageType) {
			case ChatMessageType.TEXT:
				return (
					<div className="space-y-2">
						<Label>ข้อความ</Label>
						<Textarea
							placeholder="พิมพ์ข้อความ..."
							value={value.text || ''}
							onChange={(e) => onChange({ ...value, text: e.target.value })}
							className="min-h-24"
						/>
					</div>
				)

			case ChatMessageType.STICKER:
				return (
					<div className="space-y-3">
						{value.stickerPackageId && value.stickerId ? (
							<div className="flex items-center gap-4">
								<div className="relative h-20 w-20 rounded-lg border bg-muted/30 p-2">
									<img
										src={getStickerPreviewUrl(value.stickerId)}
										alt="Selected sticker"
										className="h-full w-full object-contain"
									/>
								</div>
								<div className="flex-1 space-y-1">
									<p className="text-sm text-muted-foreground">
										Package ID: {value.stickerPackageId}
									</p>
									<p className="text-sm text-muted-foreground">
										Sticker ID: {value.stickerId}
									</p>
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => setShowStickerPicker(true)}
								>
									เปลี่ยน
								</Button>
							</div>
						) : (
							<Button
								type="button"
								variant="outline"
								className="w-full"
								onClick={() => setShowStickerPicker(true)}
							>
								<Smile className="mr-2 h-4 w-4" />
								เลือกสติกเกอร์
							</Button>
						)}
						{showStickerPicker && (
							<div className="rounded-lg border">
								<StickerPicker
									onSelect={handleStickerSelect}
									onClose={() => setShowStickerPicker(false)}
								/>
							</div>
						)}
					</div>
				)

			case ChatMessageType.IMAGE:
				return (
					<div className="space-y-3">
						{value.imageUrl ? (
							<div className="space-y-3">
								<div className="relative overflow-hidden rounded-lg border bg-muted">
									<img
										src={value.imageUrl}
										alt="Uploaded image"
										className="mx-auto max-h-48 w-auto object-contain"
									/>
								</div>
								<div className="flex items-center justify-end gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="shrink-0"
										onClick={() => onChange({ ...value, imageUrl: undefined })}
									>
										<X className="mr-2 h-4 w-4" />
										ลบรูป
									</Button>
								</div>
							</div>
						) : (
							<FileUploadButton
								accept="image/*"
								icon={<Image className="h-4 w-4" />}
								label="อัพโหลดรูปภาพ"
								isLoading={uploadMedia.isPending}
								onFileSelect={(file) => handleFileUpload(file, 'image')}
							/>
						)}
					</div>
				)

			case ChatMessageType.VIDEO:
				return (
					<div className="space-y-3">
						{value.videoUrl ? (
							<div className="space-y-3">
								<div className="relative overflow-hidden rounded-lg border bg-black">
									<video
										src={value.videoUrl}
										className="mx-auto max-h-48 w-auto"
										controls
										preload="metadata"
									/>
								</div>
								<div className="flex items-center justify-end gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="shrink-0"
										onClick={() =>
											onChange({
												...value,
												videoUrl: undefined,
												videoPreviewUrl: undefined,
											})
										}
									>
										<X className="mr-2 h-4 w-4" />
										ลบวิดีโอ
									</Button>
								</div>
								{/* Preview thumbnail */}
								{value.videoPreviewUrl &&
									value.videoPreviewUrl !== value.videoUrl && (
										<div className="space-y-2">
											<Label>รูปภาพตัวอย่าง (สร้างอัตโนมัติจากเฟรมแรก)</Label>
											<div className="relative overflow-hidden rounded-lg border bg-muted">
												<img
													src={value.videoPreviewUrl}
													alt="Video thumbnail"
													className="mx-auto max-h-32 w-auto object-contain"
												/>
											</div>
										</div>
									)}
							</div>
						) : (
							<FileUploadButton
								accept="video/*"
								icon={<Video className="h-4 w-4" />}
								label="อัพโหลดวิดีโอ"
								isLoading={uploadMedia.isPending}
								onFileSelect={(file) => handleFileUpload(file, 'video')}
							/>
						)}
					</div>
				)

			case ChatMessageType.AUDIO:
				return (
					<div className="space-y-3">
						{value.audioUrl ? (
							<div className="space-y-3">
								<div className="overflow-hidden rounded-lg border bg-muted/30 p-3">
									<audio
										src={value.audioUrl}
										controls
										className="w-full"
										preload="metadata"
									/>
								</div>
								<div className="flex items-center justify-end gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="shrink-0"
										onClick={() =>
											onChange({
												...value,
												audioUrl: undefined,
												audioDuration: undefined,
											})
										}
									>
										<X className="mr-2 h-4 w-4" />
										ลบเสียง
									</Button>
								</div>
								<div className="space-y-2">
									<Label>ระยะเวลา (มิลลิวินาที)</Label>
									<Input
										type="number"
										placeholder="ระยะเวลาเสียง (ms) - จำเป็นสำหรับ LINE"
										value={value.audioDuration || ''}
										onChange={(e) =>
											onChange({
												...value,
												audioDuration: parseInt(e.target.value) || 0,
											})
										}
									/>
									<p className="text-xs text-muted-foreground">
										ระบุระยะเวลาของไฟล์เสียงเป็นมิลลิวินาที (เช่น 5000 = 5
										วินาที)
									</p>
								</div>
							</div>
						) : (
							<FileUploadButton
								accept="audio/*"
								icon={<Mic className="h-4 w-4" />}
								label="อัพโหลดไฟล์เสียง"
								isLoading={uploadMedia.isPending}
								onFileSelect={(file) => handleFileUpload(file, 'audio')}
							/>
						)}
					</div>
				)

			case ChatMessageType.FILE:
				return (
					<div className="space-y-3">
						{value.fileUrl ? (
							<div className="flex items-center gap-4 rounded-lg border p-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
									<FileText className="h-5 w-5" />
								</div>
								<div className="flex-1">
									<p className="text-sm font-medium">{value.fileName}</p>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={() =>
										onChange({
											...value,
											fileUrl: undefined,
											fileName: undefined,
										})
									}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						) : (
							<FileUploadButton
								accept="*/*"
								icon={<FileText className="h-4 w-4" />}
								label="อัพโหลดไฟล์"
								isLoading={uploadMedia.isPending}
								onFileSelect={(file) => handleFileUpload(file, 'file')}
							/>
						)}
					</div>
				)

			case ChatMessageType.LOCATION:
				return (
					<div className="space-y-3">
						<div className="grid gap-3 sm:grid-cols-2">
							<div className="space-y-2">
								<Label>ละติจูด</Label>
								<Input
									type="number"
									step="any"
									placeholder="13.7563"
									value={value.locationLatitude ?? ''}
									onChange={(e) =>
										onChange({
											...value,
											locationLatitude: parseFloat(e.target.value) || undefined,
										})
									}
								/>
							</div>
							<div className="space-y-2">
								<Label>ลองจิจูด</Label>
								<Input
									type="number"
									step="any"
									placeholder="100.5018"
									value={value.locationLongitude ?? ''}
									onChange={(e) =>
										onChange({
											...value,
											locationLongitude:
												parseFloat(e.target.value) || undefined,
										})
									}
								/>
							</div>
						</div>
						<div className="space-y-2">
							<Label>ชื่อสถานที่</Label>
							<Input
								placeholder="ชื่อสถานที่"
								value={value.locationTitle || ''}
								onChange={(e) =>
									onChange({ ...value, locationTitle: e.target.value })
								}
							/>
						</div>
						<div className="space-y-2">
							<Label>ที่อยู่</Label>
							<Textarea
								placeholder="ที่อยู่เต็ม"
								value={value.locationAddress || ''}
								onChange={(e) =>
									onChange({ ...value, locationAddress: e.target.value })
								}
								className="min-h-16"
							/>
						</div>
					</div>
				)

			case ChatMessageType.FLEX:
				return <FlexMessageEditor value={value} onChange={onChange} />

			default:
				return null
		}
	}

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen}>
			<div
				className={cn(
					'rounded-lg border bg-card transition-shadow',
					isDragging && 'shadow-lg',
					!isValid && 'border-destructive/50',
				)}
			>
				<CollapsibleTrigger asChild>
					<div className="flex cursor-pointer items-center gap-2 p-3 hover:bg-muted/50">
						<div
							{...dragHandleProps}
							className="cursor-grab touch-none"
							onClick={(e) => e.stopPropagation()}
						>
							<GripVertical className="h-4 w-4 text-muted-foreground" />
						</div>
						<div className="flex flex-1 items-center gap-2">
							<span className="text-lg">{typeConfig?.icon}</span>
							<span className="text-sm font-medium">
								ข้อความ #{index + 1} - {typeConfig?.label}
							</span>
							{!isValid && (
								<span className="text-xs text-destructive">(ไม่สมบูรณ์)</span>
							)}
						</div>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
							onClick={(e) => {
								e.stopPropagation()
								onDelete()
							}}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				</CollapsibleTrigger>
				<CollapsibleContent>
					<div className="space-y-4 border-t p-4">
						<div className="space-y-2">
							<Label>ประเภทข้อความ</Label>
							<Select
								value={value.messageType}
								onValueChange={(v) => handleTypeChange(v as ChatMessageType)}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{MESSAGE_TYPE_CONFIGS.map((config) => (
										<SelectItem key={config.value} value={config.value}>
											<div className="flex items-center gap-2">
												<span>{config.icon}</span>
												<span>{config.label}</span>
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{renderTypeFields()}
					</div>
				</CollapsibleContent>
			</div>
		</Collapsible>
	)
}

// ==================== File Upload Button ====================

interface FileUploadButtonProps {
	accept: string
	icon: React.ReactNode
	label: string
	isLoading: boolean
	onFileSelect: (file: File) => void
}

function FileUploadButton({
	accept,
	icon,
	label,
	isLoading,
	onFileSelect,
}: FileUploadButtonProps) {
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (file) {
			onFileSelect(file)
		}
		// Reset input
		e.target.value = ''
	}

	return (
		<label className="block">
			<input
				type="file"
				accept={accept}
				onChange={handleChange}
				className="hidden"
				disabled={isLoading}
			/>
			<Button
				type="button"
				variant="outline"
				className="w-full"
				disabled={isLoading}
				asChild
			>
				<span>
					{isLoading ? (
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					) : (
						<span className="mr-2">{icon}</span>
					)}
					{isLoading ? 'กำลังอัพโหลด...' : label}
					{!isLoading && <Upload className="ml-2 h-4 w-4" />}
				</span>
			</Button>
		</label>
	)
}

// ==================== Flex Message Editor ====================

interface FlexMessageEditorProps {
	value: MessageContentInput
	onChange: (value: MessageContentInput) => void
}

function FlexMessageEditor({ value, onChange }: FlexMessageEditorProps) {
	const [jsonInput, setJsonInput] = useState(
		value.flexContents ? JSON.stringify(value.flexContents, null, 2) : '',
	)
	const [jsonError, setJsonError] = useState<string | null>(null)

	const handleJsonChange = useCallback(
		(text: string) => {
			setJsonInput(text)
			if (!text.trim()) {
				setJsonError(null)
				onChange({ ...value, flexContents: undefined })
				return
			}
			try {
				const parsed = JSON.parse(text)
				setJsonError(null)
				onChange({ ...value, flexContents: parsed })
			} catch (e) {
				setJsonError(e instanceof Error ? e.message : 'Invalid JSON')
			}
		},
		[value, onChange],
	)

	// Render flex message preview
	const renderedHtml = useMemo(() => {
		if (!value.flexContents) return null
		try {
			return renderFlexMessage(value.flexContents as FlexContainer)
		} catch {
			return null
		}
	}, [value.flexContents])

	return (
		<div className="space-y-3">
			<div className="space-y-2">
				<Label>Alt Text</Label>
				<Input
					placeholder="ข้อความแสดงแทน (สำหรับ notification)"
					value={value.flexAltText || ''}
					onChange={(e) => onChange({ ...value, flexAltText: e.target.value })}
				/>
			</div>
			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<Label>Flex Contents (JSON)</Label>
					<a
						href="https://developers.line.biz/flex-simulator/"
						target="_blank"
						rel="noopener noreferrer"
						className="text-xs text-muted-foreground hover:text-primary hover:underline"
					>
						สร้าง JSON ได้ที่ Flex Message Simulator
					</a>
				</div>
				<Textarea
					placeholder='{"type": "bubble", ...}'
					value={jsonInput}
					onChange={(e) => handleJsonChange(e.target.value)}
					className={cn(
						'min-h-32 font-mono text-sm',
						jsonError && 'border-destructive',
					)}
				/>
				{jsonError && (
					<div className="flex items-center gap-2 text-xs text-destructive">
						<AlertCircle className="h-3 w-3" />
						{jsonError}
					</div>
				)}
			</div>

			{/* Flex Message Preview */}
			{renderedHtml && (
				<div className="space-y-2">
					<Label>ตัวอย่าง</Label>
					<div className="overflow-hidden rounded-lg border bg-muted/30 p-4">
						<div
							className="flex-preview mx-auto max-w-sm"
							dangerouslySetInnerHTML={{ __html: renderedHtml }}
						/>
					</div>
				</div>
			)}

			{!renderedHtml && value.flexContents && !jsonError && (
				<div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-3 text-sm text-yellow-700">
					<AlertCircle className="mr-2 inline-block h-4 w-4" />
					ไม่สามารถแสดงตัวอย่างได้ กรุณาตรวจสอบรูปแบบ JSON
				</div>
			)}
		</div>
	)
}
